import { ApiError } from '../api';

/** Verwaltungs-Schnittstelle. Sitzung per Cookie, Änderungen mit dem Schutz-Header gegen CSRF. */

export interface TenantSettings {
  id: string;
  slug: string;
  name: string;
  customDomain: string | null;
  theme: Record<string, unknown>;
  legal: Record<string, string>;
  deviceLimit: number;
  withdrawalMode: 'waiver' | 'guarantee';
  senderEmail: string | null;
  senderName: string | null;
  stripeConnected: boolean;
  storageQuotaSeconds: number | null;
  usedSeconds: number;
}

export interface Identity {
  admin: { email: string; name: string | null; role: string; via: 'session' | 'api_key'; scopes: string[] | 'all' };
  tenant: TenantSettings;
}

export interface ConnectionRow {
  clientId: string;
  clientName: string | null;
  adminEmail: string | null;
  scopes: string[];
  connectedAt: string;
  lastUsedAt: string | null;
  active: boolean;
}

export interface ApiKeyRow {
  id: string;
  name: string;
  prefix: string;
  scopes: string[];
  createdAt: string;
  lastUsedAt: string | null;
  expiresAt: string | null;
  revokedAt: string | null;
}

export interface VideoInfo {
  id: string;
  status: string;
  progressPercent: number;
  durationSeconds: number | null;
  width: number | null;
  height: number | null;
  warnings: string[];
  rejectionCode: string | null;
  error: string | null;
  originalFilename: string;
}

export interface MaterialRow {
  id: string;
  kind: 'file' | 'link';
  title: string;
  description: string | null;
  url: string | null;
  fileName: string | null;
  sizeBytes: number | null;
  position: number;
}

export interface LessonRow {
  id: string;
  moduleId: string;
  title: string;
  shortTitle: string | null;
  speakers: string | null;
  durationSeconds: number | null;
  preview: boolean;
  position: number;
  video: VideoInfo | null;
  chapters: number;
  materials: number;
}

export interface ModuleRow {
  id: string;
  title: string;
  subtitle: string | null;
  position: number;
  releaseKind: 'immediate' | 'days_after_access' | 'date';
  releaseDays: number | null;
  releaseAt: string | null;
  lessons: LessonRow[];
}

export interface CourseSummary {
  id: string;
  slug: string;
  title: string;
  subtitle: string | null;
  kicker: string | null;
  status: 'draft' | 'published' | 'archived';
  inCatalog: boolean;
  position: number;
  imageUrl: string | null;
  lessons: number;
  customers: number;
  createdAt: string;
}

export interface CourseDetail extends CourseSummary {
  description: string | null;
  certificateEnabled: boolean;
  modules: ModuleRow[];
  materials: MaterialRow[];
  products: { id: string; title: string; priceCents: number }[];
}

export interface LessonDetail {
  id: string;
  courseId: string;
  moduleId: string;
  title: string;
  shortTitle: string | null;
  speakers: string | null;
  body: string | null;
  durationSeconds: number | null;
  preview: boolean;
  video: VideoInfo | null;
  chapters: { id: string; offsetSeconds: number; label: string }[];
  materials: MaterialRow[];
}

export interface ProductRow {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  priceCents: number;
  currency: string;
  status: 'active' | 'hidden' | 'archived';
  accessMonths: number | null;
  courseIds: string[];
  customers: number;
  position: number;
}

export interface CustomerSummary {
  id: string;
  email: string;
  name: string | null;
  createdAt: string;
  lastSeenAt: string | null;
  entitlements: number;
  completedLessons: number;
}

export interface CustomerDetail {
  id: string;
  email: string;
  name: string | null;
  createdAt: string;
  lastSeenAt: string | null;
  entitlements: { id: string; product: { id: string; title: string }; source: string; note: string | null; grantedAt: string; expiresAt: string | null; revokedAt: string | null; revokeReason: string | null }[];
  courses: { id: string; slug: string; title: string; lessons: number; completed: number; percent: number; lastSeenAt: string | null }[];
  sessions: { id: string; deviceLabel: string | null; lastSeenAt: string; lastPlayAt: string | null }[];
  mails: { kind: string; status: string; sendAt: string; sentAt: string | null; lastError: string | null }[];
}

export interface MailTemplateView {
  kind: string;
  label: string;
  when: string;
  subject: string;
  body: string;
  custom: boolean;
  required: string[];
}

async function request<T>(path: string, init?: RequestInit & { raw?: Blob | File }): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json', 'X-Requested-With': 'fetch' };
  let body: BodyInit | undefined;
  if (init?.raw) {
    body = init.raw;
    headers['Content-Type'] = init.raw.type || 'application/octet-stream';
  } else if (init?.body) {
    body = init.body;
    headers['Content-Type'] = 'application/json';
  }
  const res = await fetch(path, { method: init?.method ?? 'GET', headers: { ...headers, ...(init?.headers as Record<string, string> | undefined) }, ...(body !== undefined ? { body } : {}) });
  if (!res.ok) {
    let code = String(res.status);
    let message: string | undefined;
    try {
      const data = (await res.json()) as { error?: string; message?: string };
      code = data.error ?? code;
      message = data.message;
    } catch {
      /* kein JSON */
    }
    throw new AdminApiError(res.status, code, message);
  }
  return (await res.json()) as T;
}

export class AdminApiError extends ApiError {
  constructor(
    status: number,
    code: string,
    readonly detail?: string,
  ) {
    super(status, code);
  }
}

/** Menschliche Fehlermeldung aus einer Antwort – mit Rückfalltext. */
export function errorText(error: unknown, fallback = 'Das hat nicht geklappt. Bitte versuch es noch einmal.'): string {
  if (error instanceof AdminApiError && error.detail) return error.detail;
  if (error instanceof ApiError) {
    if (error.status === 401) return 'Deine Sitzung ist abgelaufen. Bitte melde dich neu an.';
    if (error.status === 429) return 'Zu viele Versuche. Bitte warte einen Moment.';
  }
  return fallback;
}

const json = (data: unknown) => JSON.stringify(data);

export const adminApi = {
  login: (email: string, password: string) => request<Identity>('/api/admin/login', { method: 'POST', body: json({ email, password }) }),
  logout: () => request<{ ok: true }>('/api/admin/logout', { method: 'POST' }),
  me: () => request<Identity>('/api/admin/me'),
  changePassword: (currentPassword: string, newPassword: string) => request<{ ok: true }>('/api/admin/password', { method: 'POST', body: json({ currentPassword, newPassword }) }),

  listCourses: () => request<CourseSummary[]>('/api/admin/courses'),
  createCourse: (title: string) => request<CourseDetail>('/api/admin/courses', { method: 'POST', body: json({ title }) }),
  getCourse: (id: string) => request<CourseDetail>(`/api/admin/courses/${id}`),
  updateCourse: (id: string, patch: Record<string, unknown>) => request<CourseDetail>(`/api/admin/courses/${id}`, { method: 'PATCH', body: json(patch) }),
  deleteCourse: (id: string) => request<{ ok: true }>(`/api/admin/courses/${id}`, { method: 'DELETE' }),
  duplicateCourse: (id: string) => request<CourseDetail>(`/api/admin/courses/${id}/duplicate`, { method: 'POST' }),
  reorderCourses: (ids: string[]) => request<{ ok: true }>('/api/admin/courses/order', { method: 'PUT', body: json({ ids }) }),
  setCourseImage: (id: string, file: File) => request<CourseDetail>(`/api/admin/courses/${id}/image`, { method: 'PUT', raw: file }),
  removeCourseImage: (id: string) => request<CourseDetail>(`/api/admin/courses/${id}/image`, { method: 'DELETE' }),
  createModule: (courseId: string, title: string) => request<CourseDetail>(`/api/admin/courses/${courseId}/modules`, { method: 'POST', body: json({ title }) }),
  reorder: (courseId: string, modules: { id: string; lessons: string[] }[]) => request<CourseDetail>(`/api/admin/courses/${courseId}/order`, { method: 'PUT', body: json({ modules }) }),
  updateModule: (id: string, patch: Record<string, unknown>) => request<CourseDetail>(`/api/admin/modules/${id}`, { method: 'PATCH', body: json(patch) }),
  deleteModule: (id: string) => request<CourseDetail>(`/api/admin/modules/${id}`, { method: 'DELETE' }),
  createLesson: (moduleId: string, title: string) => request<{ course: CourseDetail; lessonId: string }>(`/api/admin/modules/${moduleId}/lessons`, { method: 'POST', body: json({ title }) }),
  getLesson: (id: string) => request<LessonDetail>(`/api/admin/lessons/${id}`),
  updateLesson: (id: string, patch: Record<string, unknown>) => request<LessonDetail>(`/api/admin/lessons/${id}`, { method: 'PATCH', body: json(patch) }),
  deleteLesson: (id: string) => request<CourseDetail>(`/api/admin/lessons/${id}`, { method: 'DELETE' }),
  setChapters: (id: string, chapters: { offsetSeconds: number; label: string }[]) => request<LessonDetail>(`/api/admin/lessons/${id}/chapters`, { method: 'PUT', body: json({ chapters }) }),
  detachVideo: (id: string) => request<LessonDetail>(`/api/admin/lessons/${id}/video`, { method: 'DELETE' }),
  getVideo: (id: string) => request<VideoInfo>(`/api/admin/videos/${id}`),

  addLink: (courseId: string, lessonId: string | null, input: { title: string; description: string | null; url: string }) => request<{ ok: true }>('/api/admin/materials/link', { method: 'POST', body: json({ courseId, lessonId, ...input }) }),
  addFile: (courseId: string, lessonId: string | null, title: string, file: File) =>
    request<{ ok: true }>(`/api/admin/materials/file?courseId=${courseId}${lessonId ? `&lessonId=${lessonId}` : ''}&title=${encodeURIComponent(title)}&filename=${encodeURIComponent(file.name)}`, { method: 'PUT', raw: new Blob([file], { type: 'application/octet-stream' }), headers: { 'X-File-Type': file.type || 'application/octet-stream' } }),
  updateMaterial: (id: string, patch: Record<string, unknown>) => request<{ ok: true }>(`/api/admin/materials/${id}`, { method: 'PATCH', body: json(patch) }),
  deleteMaterial: (id: string) => request<{ ok: true }>(`/api/admin/materials/${id}`, { method: 'DELETE' }),
  reorderMaterials: (ids: string[]) => request<{ ok: true }>('/api/admin/materials/order', { method: 'PUT', body: json({ ids }) }),

  listProducts: () => request<ProductRow[]>('/api/admin/products'),
  createProduct: (title: string) => request<ProductRow>('/api/admin/products', { method: 'POST', body: json({ title }) }),
  updateProduct: (id: string, patch: Record<string, unknown>) => request<ProductRow>(`/api/admin/products/${id}`, { method: 'PATCH', body: json(patch) }),
  deleteProduct: (id: string) => request<{ ok: true }>(`/api/admin/products/${id}`, { method: 'DELETE' }),

  listCustomers: (q: string) => request<CustomerSummary[]>(`/api/admin/customers?q=${encodeURIComponent(q)}`),
  createCustomer: (email: string, name: string | null) => request<CustomerDetail>('/api/admin/customers', { method: 'POST', body: json({ email, name }) }),
  getCustomer: (id: string) => request<CustomerDetail>(`/api/admin/customers/${id}`),
  updateCustomer: (id: string, patch: Record<string, unknown>) => request<CustomerDetail>(`/api/admin/customers/${id}`, { method: 'PATCH', body: json(patch) }),
  deleteCustomer: (id: string) => request<{ ok: true }>(`/api/admin/customers/${id}`, { method: 'DELETE' }),
  grant: (customerId: string, productId: string, note: string | null, sendWelcome: boolean) => request<CustomerDetail>(`/api/admin/customers/${customerId}/entitlements`, { method: 'POST', body: json({ productId, note, sendWelcome }) }),
  sendLoginLink: (customerId: string) => request<{ ok: true }>(`/api/admin/customers/${customerId}/login-link`, { method: 'POST' }),
  revoke: (entitlementId: string, reason: string | null) => request<CustomerDetail>(`/api/admin/entitlements/${entitlementId}/revoke`, { method: 'POST', body: json({ reason }) }),
  restore: (entitlementId: string) => request<CustomerDetail>(`/api/admin/entitlements/${entitlementId}/restore`, { method: 'POST' }),

  updateTenant: (patch: Record<string, unknown>) => request<TenantSettings>('/api/admin/tenant', { method: 'PATCH', body: json(patch) }),
  setLogo: (variant: 'light' | 'dark' | 'icon', file: File) => request<TenantSettings>(`/api/admin/tenant/logo?variant=${variant}`, { method: 'PUT', raw: file }),
  removeLogo: (variant: 'light' | 'dark' | 'icon') => request<TenantSettings>(`/api/admin/tenant/logo?variant=${variant}`, { method: 'DELETE' }),

  listApiKeys: () => request<{ keys: ApiKeyRow[]; scopes: { scope: string; title: string; description: string }[] }>('/api/admin/api-keys'),
  createApiKey: (name: string, scopes: string[], expiresInDays: number | null) => request<{ key: string; row: ApiKeyRow }>('/api/admin/api-keys', { method: 'POST', body: json({ name, scopes, expiresInDays }) }),
  revokeApiKey: (id: string) => request<{ ok: true }>(`/api/admin/api-keys/${id}`, { method: 'DELETE' }),
  listConnections: () => request<{ connections: ConnectionRow[]; scopes: { scope: string; title: string; description: string }[] }>('/api/admin/connections'),
  disconnect: (clientId: string) => request<{ ok: true }>(`/api/admin/connections/${clientId}`, { method: 'DELETE' }),
  auditLog: (limit = 100) => request<{ actorKind: string; actorLabel: string | null; method: string; path: string; status: number; at: string }[]>(`/api/admin/audit-log?limit=${limit}`),

  listMails: () => request<{ mails: MailTemplateView[]; placeholders: { key: string; description: string }[] }>('/api/admin/mails'),
  saveMail: (kind: string, template: { subject: string; body: string }) => request<{ ok: true }>(`/api/admin/mails/${kind}`, { method: 'PUT', body: json(template) }),
  resetMail: (kind: string) => request<{ ok: true }>(`/api/admin/mails/${kind}`, { method: 'DELETE' }),
  previewMail: (kind: string, template: { subject: string; body: string }) => request<{ subject: string; text: string }>(`/api/admin/mails/${kind}/preview`, { method: 'POST', body: json(template) }),
};
